// Смена этапа воронки. Панель, а не модалка: этап меняется туда и обратно, необратимого в
// нём нет — необратимо только оформление, и оно живёт отдельной модалкой.
//
// Этапа «оформлен» в списке нет намеренно, и это не косметика. Его выставляют кадры переводом
// в сотрудники (тз 5.8.1); поставить его отдельным действием значило бы отметить человека
// принятым БЕЗ карточки сотрудника — без ФИО в реестре, без тарифа и без табеля. Сервер такого
// значения и не принимает.
//
// Причина отказа — обязательный справочник при «не согласован» и свободный текст в остальных
// случаях. Свободный текст не обязателен на сервере, но подписан так, чтобы его хотелось
// заполнить: без него в истории останется «новый → лист ожидания» без единого слова о том,
// почему, и через полгода это спрашивают у того, кто уже не помнит.

import type { FormEvent } from 'react';
import { useState } from 'react';

import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { Skeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextArea } from '@/shared/ui/text-area';

import type { CandidateStatus, RejectionReason } from '../api/candidates';
import { REJECTION_REASONS, SETTABLE_STATUSES, isCandidateStatus } from '../api/candidates';
import { GateNote, NoPermissionNote, useCan } from '../components/gates';
import { CANDIDATE_STATUS_TITLES, REJECTION_REASON_TITLES } from '../dictionaries';
import { useCandidateQuery, useCandidateStatusMutation } from '../queries';

const EDIT_PERMISSION = 'hiring.candidate.edit';

export function CandidateStatusPanel({
  candidateId,
  onClose,
}: {
  candidateId: string;
  onClose: () => void;
}) {
  const allowed = useCan(EDIT_PERMISSION);
  const кандидат = useCandidateQuery(candidateId);
  const мутация = useCandidateStatusMutation(candidateId);

  const [этап, setЭтап] = useState(NO_VALUE);
  const [причинаОтказа, setПричинаОтказа] = useState(NO_VALUE);
  const [пояснение, setПояснение] = useState('');
  const [ошибкаЭтапа, setОшибкаЭтапа] = useState<string | undefined>(undefined);
  const [ошибкаПричины, setОшибкаПричины] = useState<string | undefined>(undefined);
  const [отказ, setОтказ] = useState<string | null>(null);

  const текущий = кандидат.data?.status;
  const отказВыбран = этап === 'rejected';

  function отправить(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setОтказ(
        'Менять этап может рекрутёр и кадры. Попросите администратора выдать право на правку карточек.',
      );
      return;
    }
    if (мутация.isPending) {
      setОтказ('Этап уже меняется, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }
    if (!isCandidateStatus(этап) || этап === 'hired') {
      setОшибкаЭтапа('Выберите новый этап — иначе непонятно, куда двигать кандидата.');
      return;
    }
    if (этап === текущий) {
      setОшибкаЭтапа(
        `Кандидат уже на этапе «${CANDIDATE_STATUS_TITLES[этап]}». Выберите другой — запись «из А в А» истории ничего не добавит.`,
      );
      return;
    }
    if (отказВыбран && причинаОтказа === NO_VALUE) {
      setОшибкаПричины(
        'При отказе причина обязательна: по ней потом считают, почему люди не доходят до объекта.',
      );
      return;
    }
    setОшибкаЭтапа(undefined);
    setОшибкаПричины(undefined);
    setОтказ(null);

    мутация.mutate(
      {
        status: этап as Exclude<CandidateStatus, 'hired'>,
        reason: пояснение.trim() === '' ? null : пояснение.trim(),
        rejection_reason:
          отказВыбран && причинаОтказа !== NO_VALUE ? (причинаОтказа as RejectionReason) : null,
      },
      { onSuccess: onClose },
    );
  }

  return (
    <form onSubmit={отправить} noValidate className='flex flex-col gap-3'>
      {/* Скелет показывается ТОЛЬКО пока запрос идёт. Условие «данных нет» держало бы его
          вечно: карточка может не приехать вовсе — например, панель открыли по ссылке на
          кандидата, которого больше не видно по правам. Вечный скелет читается как «сейчас
          догрузится» и не догружается никогда. */}
      {кандидат.data === undefined && кандидат.isPending ? (
        <Skeleton className='h-4 w-56' />
      ) : кандидат.data === undefined ? null : (
        <p className='text-paragraph-xs text-text-sub-600'>
          {`${кандидат.data.display_name} · сейчас «${CANDIDATE_STATUS_TITLES[кандидат.data.status]}»`}
        </p>
      )}

      {!allowed && (
        <NoPermissionNote что='менять этап воронки' кто='Воронку ведут рекрутёр и кадры.' />
      )}

      <SelectField
        id='candidate-stage'
        label='Новый этап'
        required
        value={этап}
        onChange={setЭтап}
        options={SETTABLE_STATUSES.map((code) => ({
          value: code,
          label: CANDIDATE_STATUS_TITLES[code],
        }))}
        placeholder='Выберите этап'
        error={ошибкаЭтапа}
        hint='Этапа «оформлен» здесь нет: он ставится только переводом в сотрудники, на вкладке «Оформление».'
      />

      {отказВыбран && (
        <SelectField
          id='candidate-rejection'
          label='Причина отказа'
          required
          value={причинаОтказа}
          onChange={setПричинаОтказа}
          options={REJECTION_REASONS.map((code) => ({
            value: code,
            label: REJECTION_REASON_TITLES[code],
          }))}
          placeholder='Выберите причину'
          error={ошибкаПричины}
          hint='Справочник, а не свободный текст: по нему считается, на каком шаге воронка теряет людей.'
        />
      )}

      <TextArea
        id='candidate-status-reason'
        label='Пояснение'
        value={пояснение}
        onChange={setПояснение}
        placeholder='Например: попросил перезвонить после 15 числа'
        hint='Останется в истории этапов. Без него через полгода никто не вспомнит, почему этап сменился.'
      />

      {мутация.isError && <ErrorAlert error={мутация.error} />}
      {отказ !== null && <GateNote>{отказ}</GateNote>}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {мутация.isPending ? 'Меняем этап…' : 'Сменить этап'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
